/**
 * SiteFooter 컴포넌트
 * @description 웹에만 있는 바닥줄 — 앱은 탭 바가 그 자리를 채운다. 생김새와 문구는 FanTrip 공통
 *   푸터(`fantrip/Dev/FE` `widgets/site-footer`, 영문 `common.footer`)를 그대로 따른다: 이름 ·
 *   법적 링크 · 사업자 정보(왼쪽)와 대표전화 · SNS(오른쪽). 링크가 가는 곳은 이 사이트의
 *   약관 · 개인정보 · 문의 화면이다.
 * @example
 * <Screen>…<SiteFooter /></Screen>
 */
import Link from "next/link";
import { memo } from "react";

import { InstagramGlyph, XGlyph, YoutubeGlyph } from "./SiteFooter.icons";
import {
  SITE_FOOTER_SNS_ICON_SIZE,
  siteFooterStyles,
} from "./SiteFooter.styles";

import { SOCIAL_LINKS } from "@/shared/config";
import { routes } from "@/shared/config/routes";

const COPY = {
  brand: "FanWork",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  contact: "Contact",
  company: "Company Name: Data Town Inc.  CEOs: Hyuntae Lim, Jinhwa Lee",
  address:
    "Address: Rm 1008, 10F (Tourism Business Support Center), 40, Cheonggyecheon-ro, Jung-gu, Seoul, Republic of Korea",
  tel: "Tel",
} as const;

const TEL = "070-8080-0265";

const LINKS = [
  { label: COPY.terms, href: routes.legal.terms },
  { label: COPY.privacy, href: routes.legal.privacy },
  { label: COPY.contact, href: routes.contact },
] as const;

/** 폰은 한 줄씩, 태블릿부터 "  ·  " 로 이어 쓴다 */
const REGISTRATIONS = [
  "Business Registration No.: 540-86-03038",
  "Tourism Business Registration Number: No. 2025-000064",
  "Mail Order Sales Approval No.: 2023-SeoulGeumcheon-1127",
  "Chief Privacy Officer: Hyuntae Lim",
] as const;

const SNS_ITEMS = [
  { label: "Instagram", Glyph: InstagramGlyph, href: SOCIAL_LINKS.instagram },
  { label: "X", Glyph: XGlyph, href: SOCIAL_LINKS.x },
  { label: "YouTube", Glyph: YoutubeGlyph, href: SOCIAL_LINKS.youtube },
] as const;

const SiteFooter = memo(() => (
  <footer className={siteFooterStyles.root}>
    <div className={siteFooterStyles.row}>
      <div className={siteFooterStyles.company}>
        <p className={siteFooterStyles.brand}>{COPY.brand}</p>
        <ul className={siteFooterStyles.links}>
          {LINKS.map((link) => (
            <li key={link.href}>
              <Link href={link.href} className={siteFooterStyles.link}>
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className={siteFooterStyles.info}>
          <p>{COPY.company}</p>
          <p>{COPY.address}</p>
          <p>
            {REGISTRATIONS.map((line, i) => (
              <span key={line}>
                {i > 0 && (
                  <span className={siteFooterStyles.registrationSeparator}>
                    {"  ·  "}
                  </span>
                )}
                <span className={siteFooterStyles.registration}>{line}</span>
              </span>
            ))}
          </p>
        </div>
      </div>

      <div className={siteFooterStyles.contact}>
        <div className={siteFooterStyles.tel}>
          <p className={siteFooterStyles.telLabel}>{COPY.tel}</p>
          <a href={`tel:${TEL}`} className={siteFooterStyles.telNumber}>
            {TEL}
          </a>
        </div>
        <ul className={siteFooterStyles.sns}>
          {SNS_ITEMS.map(({ label, Glyph, href }) => (
            <li key={label}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className={siteFooterStyles.snsLink}
              >
                <Glyph size={SITE_FOOTER_SNS_ICON_SIZE} />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </footer>
));

SiteFooter.displayName = "SiteFooter";

export default SiteFooter;
